import { DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Alert, Skeleton } from 'antd';
import type { CSSProperties, HTMLAttributes, ReactNode, Ref } from 'react';

export interface DragHandleProps extends HTMLAttributes<HTMLElement> {
  ref?: Ref<HTMLElement>;
}

export interface DraggableSlotsGridProps<TSlot> {
  slots: TSlot[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onSwap: (positionA: number, positionB: number) => void | Promise<void>;
  getSlotKey: (slot: TSlot) => string;
  getSlotPosition: (slot: TSlot) => number;
  renderSlot: (slot: TSlot, isDragging: boolean, dragHandleProps: DragHandleProps) => ReactNode;
  skeletonCount?: number;
  skeletonHeight?: number;
  errorMessage?: string;
}

const GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill,minmax(190px,1fr))',
  gap: 14,
} as const;

interface SortableSlotProps<TSlot> {
  slot: TSlot;
  slotKey: string;
  renderSlot: DraggableSlotsGridProps<TSlot>['renderSlot'];
}

function SortableSlot<TSlot>({ slot, slotKey, renderSlot }: SortableSlotProps<TSlot>) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: slotKey,
  });

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    outline: 'none',
    ...(isDragging
      ? { position: 'relative', zIndex: 10, boxShadow: '0 14px 32px rgba(0,0,0,0.35)', borderRadius: 12 }
      : {}),
  };

  return (
    <div ref={setNodeRef} {...attributes} style={style}>
      {renderSlot(slot, isDragging, { ref: setActivatorNodeRef, ...listeners })}
    </div>
  );
}

// Shared, data-agnostic drag-to-swap grid used by both the Browser and Point "carousel
// slots" admin pages — the two features never import from each other (module isolation),
// so this lives here instead, generic over each feature's own slot/banner shape. Card
// content (image, status tag, buttons) is intentionally NOT unified here: it differs
// enough between the two feature's data shapes that forcing a shared shape would cost
// more than the duplication it removes. Only the drag-to-swap mechanics are shared.
export function DraggableSlotsGrid<TSlot>({
  slots,
  isLoading,
  isError,
  onRetry,
  onSwap,
  getSlotKey,
  getSlotPosition,
  renderSlot,
  skeletonCount = 5,
  skeletonHeight = 220,
  errorMessage = "Couldn't load slots",
}: DraggableSlotsGridProps<TSlot>) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const activeSlot = slots.find((s) => getSlotKey(s) === active.id);
    const overSlot = slots.find((s) => getSlotKey(s) === over.id);
    if (!activeSlot || !overSlot) return;
    void onSwap(getSlotPosition(activeSlot), getSlotPosition(overSlot));
  };

  if (isLoading) {
    return (
      <div style={GRID_STYLE}>
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <Skeleton.Node key={i} active style={{ width: '100%', height: skeletonHeight }} />
        ))}
      </div>
    );
  }

  if (isError) {
    return <Alert type="error" showIcon message={errorMessage} action={<a onClick={onRetry}>Retry</a>} />;
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <SortableContext items={slots.map(getSlotKey)} strategy={rectSortingStrategy}>
        <div style={GRID_STYLE}>
          {slots.map((slot) => (
            <SortableSlot key={getSlotKey(slot)} slot={slot} slotKey={getSlotKey(slot)} renderSlot={renderSlot} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
